import { motion } from "framer-motion";
import { ArrowRight, Bed, Bath, Maximize, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { properties } from "@/data/properties";
import { useRegion } from "@/contexts/RegionContext";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 },
  },
};

export const FeaturedProperties = () => {
  const { formatArea, areaUnit, formatPrice } = useRegion();
  
  return (
    <section id="properties" className="py-24 lg:py-32 bg-background">
      <div className="container mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 lg:mb-20"
        >
          <p className="font-body text-sm uppercase tracking-[0.3em] text-gold mb-4">
            Curated Collection
          </p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-foreground">
            Estates of
            <span className="block italic font-medium">Enduring Value</span>
          </h2>
          <p className="font-body text-muted-foreground mt-6 max-w-xl mx-auto">
            Properties selected for provenance, position, and generational significance.
          </p>
          <div className="w-20 h-px bg-gradient-gold mx-auto mt-8" />
        </motion.div>

        {/* Properties Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 lg:grid-cols-3 gap-8"
        >
          {properties.map((property) => (
            <motion.article
              key={property.id}
              variants={itemVariants}
              whileHover={{ 
                y: -12,
                scale: 1.02,
                transition: { duration: 0.3, ease: "easeOut" }
              }}
              className="group cursor-pointer bg-card rounded-sm overflow-hidden shadow-elegant hover:shadow-premium transition-shadow duration-500"
            >
              <Link to={`/property/${property.id}`}>
                {/* Image Container */}
                <div className="relative overflow-hidden aspect-[4/5] image-zoom">
                  <img
                    src={property.image}
                    alt={`${property.title} - Luxury property in ${property.location}`}
                    className="w-full h-full object-cover"
                  />
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-charcoal/60 via-charcoal/0 to-charcoal/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  {/* Price Tag */}
                  <motion.div 
                    className="absolute top-6 left-6"
                    whileHover={{ scale: 1.05 }}
                  >
                    <span className="bg-cream/95 backdrop-blur-sm px-4 py-2 font-body text-sm font-semibold text-charcoal tracking-wide shadow-lg">
                      {formatPrice(property.price)}
                    </span>
                  </motion.div>

                  {/* Featured Badge */}
                  {property.featured && (
                    <div className="absolute top-6 right-6">
                      <span className="bg-gradient-gold px-4 py-2 font-body text-xs uppercase tracking-[0.15em] text-cream shadow-lg">
                        Featured
                      </span>
                    </div>
                  )}

                  {/* View Button */}
                  <div className="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-all duration-400 transform translate-y-4 group-hover:translate-y-0">
                    <Button variant="hero-solid" size="default">
                      View Details
                      <ArrowRight size={16} />
                    </Button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-3">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <MapPin size={14} />
                    <span className="font-body text-sm tracking-wide">
                      {property.location}
                    </span>
                  </div>
                  <h3 className="font-display text-2xl lg:text-3xl text-foreground group-hover:text-gold transition-colors duration-300">
                    {property.title}
                  </h3>
                  
                  {/* Specs */}
                  <div className="flex items-center gap-6 pt-2 text-muted-foreground">
                    <div className="flex items-center gap-2 group-hover:text-foreground transition-colors duration-300">
                      <Bed size={16} />
                      <span className="font-body text-sm">{property.beds} Beds</span>
                    </div>
                    <div className="flex items-center gap-2 group-hover:text-foreground transition-colors duration-300">
                      <Bath size={16} />
                      <span className="font-body text-sm">{property.baths} Baths</span>
                    </div>
                    <div className="flex items-center gap-2 group-hover:text-foreground transition-colors duration-300">
                      <Maximize size={16} />
                      <span className="font-body text-sm">{formatArea(property.sqm)} {areaUnit}</span>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.article>
          ))}
        </motion.div>

        {/* View All Button */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-16"
        >
          <Button variant="luxury-outline" size="xl" asChild>
            <Link to="/properties">
              View All Properties
              <ArrowRight size={18} />
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>
  );
};
